import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, IndianRupee, UserPlus, Stethoscope, Truck, AlertTriangle, ArrowUpRight, FileCheck2 } from "lucide-react";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import SentimentRadarCard from "../../components/SentimentRadarCard";
import { api } from "../../api/client";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const STATUS_PILL = {
    created: "bg-slate-100 text-slate-600",
    payment_pending: "bg-amber-100 text-amber-700",
    paid: "bg-emerald-100 text-emerald-700",
    packed: "bg-blue-100 text-blue-700",
    dispatched: "bg-indigo-100 text-indigo-700",
    delivered: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-rose-100 text-rose-600",
};

const RX_PILL = {
    pending_review: "bg-amber-100 text-amber-700",
    approved: "bg-emerald-100 text-emerald-700",
    rejected: "bg-rose-100 text-rose-600",
};

const Kpi = ({ icon: Icon, label, value, hint, accent = "brand", testid }) => {
    const accentMap = {
        brand: "from-brand-600 to-brand-400",
        emerald: "from-emerald-600 to-emerald-400",
        amber: "from-amber-600 to-amber-400",
        rose: "from-rose-600 to-rose-400",
        slate: "from-slate-700 to-slate-500",
    };
    return (
        <div
            className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-soft transition-all hover:shadow-md"
            data-testid={testid}
        >
            <div className={`absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br ${accentMap[accent]} opacity-10 transition-opacity group-hover:opacity-20`} />
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
                    <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">{value}</p>
                    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
                </div>
                <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br ${accentMap[accent]} text-white shadow-sm`}>
                    <Icon className="h-4 w-4" />
                </span>
            </div>
        </div>
    );
};

const QuickAction = ({ to, icon: Icon, title, count, accent }) => (
    <Link
        to={to}
        className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-soft"
        data-testid={`quick-action-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
        <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg bg-${accent}-50 text-${accent}-700`}>
            <Icon className="h-4 w-4" />
        </span>
        <div className="flex-1">
            <p className="text-sm font-medium text-slate-900">{title}</p>
            <p className="text-xs text-slate-500">{count} pending</p>
        </div>
        <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-brand-700" />
    </Link>
);

const DashboardContent = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get("/admin/dashboard");
                setData(res.data);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading || !data) {
        return (
            <div className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {[0,1,2,3].map((i) => <div key={i} className="h-28 skeleton rounded-2xl" />)}
                </div>
                <div className="h-64 skeleton rounded-2xl" />
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="admin-dashboard">
            <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Kpi icon={ShoppingBag} label="Today's orders" value={data.today.orders} hint="Across all channels" accent="brand" testid="kpi-today-orders" />
                <Kpi icon={IndianRupee} label="Today's revenue" value={inr(data.today.revenue)} hint="Paid + dispatched" accent="emerald" testid="kpi-today-revenue" />
                <Kpi icon={UserPlus} label="New customers" value={data.today.new_users} hint="In the last 24h" accent="slate" testid="kpi-today-users" />
                <Kpi icon={Stethoscope} label="Rx pending" value={data.queues.prescriptions_pending} hint="Awaiting pharmacist" accent={data.queues.prescriptions_pending > 0 ? "amber" : "slate"} testid="kpi-rx-pending" />
            </section>

            <section className="grid gap-3 sm:grid-cols-3">
                <QuickAction to="/admin/prescriptions?status=pending_review" icon={FileCheck2} title="Verify prescriptions" count={data.queues.prescriptions_pending} accent="amber" />
                <QuickAction to="/admin/orders?status=paid" icon={Truck} title="Pack & dispatch" count={data.queues.orders_to_dispatch} accent="blue" />
                <QuickAction to="/admin/inventory" icon={AlertTriangle} title="Low stock items" count={data.queues.low_stock_items} accent="rose" />
            </section>

            {/* v2E — AI sentiment radar */}
            <section className="grid gap-6 lg:grid-cols-[1fr_320px]">
                <div /> {/* placeholder for future ops widget */}
                <SentimentRadarCard />
            </section>

            <section className="grid gap-6 lg:grid-cols-2">
                {/* Recent orders */}
                <div className="rounded-2xl border border-slate-200 bg-white shadow-soft" data-testid="dashboard-recent-orders">
                    <header className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Latest activity</p>
                            <h3 className="text-sm font-semibold text-slate-900">Recent orders</h3>
                        </div>
                        <Link to="/admin/orders" className="text-xs font-medium text-brand-700 hover:underline">View all</Link>
                    </header>
                    {data.recent_orders.length === 0 ? (
                        <p className="px-5 py-8 text-center text-sm text-slate-400">No orders yet.</p>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {data.recent_orders.map((o) => (
                                <li key={o.id} className="flex items-center gap-3 px-5 py-3">
                                    <div className="min-w-0 flex-1">
                                        <Link to={`/admin/orders/${o.id}`} className="line-clamp-1 text-sm font-medium text-slate-900 hover:text-brand-700">{o.order_no}</Link>
                                        <p className="line-clamp-1 text-xs text-slate-500">{o.customer_name}</p>
                                    </div>
                                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_PILL[o.status] || "bg-slate-100"}`}>{o.status}</span>
                                    <p className="w-20 shrink-0 text-right text-sm font-semibold tabular-nums text-slate-900">{inr(o.total)}</p>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Recent prescriptions */}
                <div className="rounded-2xl border border-slate-200 bg-white shadow-soft" data-testid="dashboard-recent-prescriptions">
                    <header className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Verification queue</p>
                            <h3 className="text-sm font-semibold text-slate-900">Recent prescriptions</h3>
                        </div>
                        <Link to="/admin/prescriptions" className="text-xs font-medium text-brand-700 hover:underline">Open workspace</Link>
                    </header>
                    {data.recent_prescriptions.length === 0 ? (
                        <p className="px-5 py-8 text-center text-sm text-slate-400">Inbox is empty.</p>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {data.recent_prescriptions.map((rx) => (
                                <li key={rx.id} className="flex items-center gap-3 px-5 py-3">
                                    <div className="min-w-0 flex-1">
                                        <Link to={`/admin/prescriptions/${rx.id}`} className="line-clamp-1 text-sm font-medium text-slate-900 hover:text-brand-700">
                                            {rx.doctor_name || "Unknown doctor"}
                                        </Link>
                                        <p className="line-clamp-1 text-xs text-slate-500">{rx.customer_name}</p>
                                    </div>
                                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${RX_PILL[rx.verification_status] || "bg-slate-100"}`}>
                                        {rx.verification_status.replace("_", " ")}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>
        </div>
    );
};

const AdminDashboard = () => (
    <AdminAuthGuard>
        <AdminLayout title="Dashboard" subtitle="Real-time pulse of PILLS operations">
            <DashboardContent />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminDashboard;
